import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  LogIn, Ticket, Calendar, MessageSquare, UserCog, CreditCard,
  Clock, Filter, List, LayoutGrid, Activity, ChevronDown,
  Search, RefreshCw, X, TrendingUp, AlertCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ACTIVITY_ICONS = {
  login: LogIn,
  ticket_purchase: Ticket,
  event_created: Calendar,
  complaint_filed: MessageSquare,
  profile_update: UserCog,
  payment_method_added: CreditCard,
};

const ACTIVITY_COLORS = {
  login: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
  ticket_purchase: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/30' },
  event_created: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
  complaint_filed: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  profile_update: { bg: 'bg-cyan-500/10', text: 'text-cyan-500', border: 'border-cyan-500/30' },
  payment_method_added: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/30' },
};

const ACTIVITY_LABELS = {
  login: 'Login',
  ticket_purchase: 'Ticket Purchase',
  event_created: 'Event Created',
  complaint_filed: 'Complaint Filed',
  profile_update: 'Profile Update',
  payment_method_added: 'Payment',
};

export default function UserActivityView({ user, open, onClose }) {
  const [activities, setActivities] = useState([]);
  const [activityStats, setActivityStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('timeline'); // timeline, list, table
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('365'); // days
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (open && user) {
      fetchActivities();
    }
  }, [open, user, dateRange]);

  const fetchActivities = async () => {
    if (!user?.user_id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/users/${user.user_id}/activities`,
        { 
          params: { days: parseInt(dateRange), limit: 200 },
           
        }
      );
      setActivities(response.data.activities || []);
      setActivityStats(response.data.stats || {});
      setUserData(response.data.user);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load user activities');
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activities.filter(activity => {
    // Filter by type
    if (filterType !== 'all' && activity.activity_type !== filterType) {
      return false;
    }
    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      return (
        activity.description?.toLowerCase().includes(searchLower) ||
        activity.activity_type?.toLowerCase().includes(searchLower) ||
        JSON.stringify(activity.metadata || {}).toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getActivityIcon = (type) => {
    const Icon = ACTIVITY_ICONS[type] || Activity;
    return Icon;
  };

  const getActivityColor = (type) => {
    return ACTIVITY_COLORS[type] || ACTIVITY_COLORS.login;
  };

  const renderTimelineView = () => (
    <ScrollArea className="h-[500px] pr-4">
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
        
        <div className="space-y-4">
          {filteredActivities.map((activity, index) => {
            const Icon = getActivityIcon(activity.activity_type);
            const colors = getActivityColor(activity.activity_type);
            const isLast = index === filteredActivities.length - 1;
            
            return (
              <div 
                key={`${activity.activity_type}-${activity.created_at}-${index}`}
                className="relative flex gap-4 pl-2"
                data-testid={`activity-item-${index}`}
              >
                {/* Icon bubble */}
                <div className={`relative z-10 w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center ring-4 ring-background`}>
                  <Icon className={`w-4 h-4 ${colors.text}`} />
                </div>
                
                {/* Content */}
                <div className={`flex-1 pb-4 ${!isLast ? 'border-b border-border/50' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm">{activity.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`${colors.border} text-xs`}>
                          {ACTIVITY_LABELS[activity.activity_type] || activity.activity_type}
                        </Badge>
                        {activity.metadata?.event_title && (
                          <span className="text-xs text-muted-foreground">
                            {activity.metadata.event_title}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-muted-foreground">
                        {activity.created_at && formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </p>
                      <p className="text-xs text-muted-foreground/60">
                        {activity.created_at && format(new Date(activity.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Metadata details */}
                  {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                    <div className="mt-2 p-2 rounded-lg bg-muted/50 text-xs">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {activity.metadata.amount && (
                          <div>
                            <span className="text-muted-foreground">Amount:</span>{' '}
                            <span className="font-medium text-emerald-500">₦{activity.metadata.amount.toLocaleString()}</span>
                          </div>
                        )}
                        {activity.metadata.quantity && (
                          <div>
                            <span className="text-muted-foreground">Quantity:</span>{' '}
                            <span className="font-medium">{activity.metadata.quantity}</span>
                          </div>
                        )}
                        {activity.metadata.category && (
                          <div>
                            <span className="text-muted-foreground">Category:</span>{' '}
                            <span className="font-medium">{activity.metadata.category}</span>
                          </div>
                        )}
                        {activity.metadata.status && (
                          <div>
                            <span className="text-muted-foreground">Status:</span>{' '}
                            <Badge variant="outline" className="text-xs ml-1">{activity.metadata.status}</Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );

  const renderListView = () => (
    <ScrollArea className="h-[500px] pr-4">
      <div className="space-y-2">
        {filteredActivities.map((activity, index) => {
          const Icon = getActivityIcon(activity.activity_type);
          const colors = getActivityColor(activity.activity_type);
          
          return (
            <div 
              key={`${activity.activity_type}-${activity.created_at}-${index}`}
              className={`flex items-center gap-3 p-3 rounded-lg border ${colors.border} ${colors.bg}`}
              data-testid={`activity-list-item-${index}`}
            >
              <div className={`w-8 h-8 rounded-full bg-background flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${colors.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{activity.description}</p>
                <p className="text-xs text-muted-foreground">
                  {activity.created_at && format(new Date(activity.created_at), 'MMM d, yyyy • h:mm a')}
                </p>
              </div>
              <Badge variant="outline" className="shrink-0 text-xs">
                {ACTIVITY_LABELS[activity.activity_type]}
              </Badge>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );

  const renderTableView = () => (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Type</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Details</TableHead>
            <TableHead className="text-right">Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredActivities.slice(0, 50).map((activity, index) => {
            const Icon = getActivityIcon(activity.activity_type);
            const colors = getActivityColor(activity.activity_type);
            
            return (
              <TableRow key={`${activity.activity_type}-${activity.created_at}-${index}`} data-testid={`activity-table-row-${index}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${colors.text}`} />
                    <span className="text-xs">{ACTIVITY_LABELS[activity.activity_type]}</span>
                  </div>
                </TableCell>
                <TableCell className="font-medium">{activity.description}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {activity.metadata?.event_title && <span>{activity.metadata.event_title}</span>}
                  {activity.metadata?.amount && <span className="text-emerald-500 ml-2">₦{activity.metadata.amount.toLocaleString()}</span>}
                </TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">
                  {activity.created_at && format(new Date(activity.created_at), 'MMM d, yyyy')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {filteredActivities.length > 50 && (
        <div className="p-3 text-center text-sm text-muted-foreground border-t">
          Showing 50 of {filteredActivities.length} activities. Use filters to narrow results.
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden" data-testid="user-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Activity className="w-5 h-5" />
            User Activity History
          </DialogTitle>
        </DialogHeader>

        {/* User Info Card */}
        {userData && (
          <Card className="bg-muted/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold">
                    {userData.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold">{userData.name}</h3>
                    <p className="text-sm text-muted-foreground">{userData.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline">{userData.role}</Badge>
                      {userData.admin_role && (
                        <Badge className="bg-red-500">{userData.admin_role}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{activities.length}</p>
                  <p className="text-xs text-muted-foreground">Total Activities</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity Stats */}
        <div className="grid grid-cols-6 gap-2">
          {Object.entries(activityStats).map(([type, count]) => {
            const Icon = getActivityIcon(type);
            const colors = getActivityColor(type);
            return (
              <Card key={type} className={`${colors.bg} ${colors.border} border cursor-pointer hover:opacity-80 transition-opacity`} onClick={() => setFilterType(filterType === type ? 'all' : type)}>
                <CardContent className="p-2 text-center">
                  <Icon className={`w-4 h-4 ${colors.text} mx-auto mb-1`} />
                  <p className="text-lg font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground truncate">{ACTIVITY_LABELS[type]}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* View Mode Toggle */}
          <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v)} data-testid="view-mode-toggle">
            <ToggleGroupItem value="timeline" aria-label="Timeline view" data-testid="view-timeline-btn">
              <TrendingUp className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view" data-testid="view-list-btn">
              <List className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="table" aria-label="Table view" data-testid="view-table-btn">
              <LayoutGrid className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Date Range */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <Clock className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>

          {/* Activity Type Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px]" data-testid="activity-type-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Activities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              <SelectItem value="login">Logins</SelectItem>
              <SelectItem value="ticket_purchase">Ticket Purchases</SelectItem>
              <SelectItem value="event_created">Events Created</SelectItem>
              <SelectItem value="complaint_filed">Complaints</SelectItem>
              <SelectItem value="profile_update">Profile Updates</SelectItem>
              <SelectItem value="payment_method_added">Payments</SelectItem>
            </SelectContent>
          </Select>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="activity-search-input"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setSearchQuery('')}
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>

          {/* Refresh */}
          <Button variant="outline" size="icon" onClick={fetchActivities} disabled={loading} data-testid="refresh-activities-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <Separator />

        {/* Activity Content */}
        {loading ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading activities...</p>
            </div>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px] text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-1">No activities found</h3>
            <p className="text-sm text-muted-foreground">
              {filterType !== 'all' || searchQuery 
                ? 'Try adjusting your filters or search query.'
                : 'This user has no recorded activities in the selected time period.'}
            </p>
          </div>
        ) : (
          <>
            {viewMode === 'timeline' && renderTimelineView()}
            {viewMode === 'list' && renderListView()}
            {viewMode === 'table' && renderTableView()}
          </>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <span>
            Showing {filteredActivities.length} of {activities.length} activities
          </span>
          <span>
            Data from the last {dateRange} days
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
